import { CopyAll, Link, LinkOff } from '@mui/icons-material'
import { Box, FilledInput, IconButton, InputAdornment, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateSystemUserCache, useGetUserInviteURL } from 'src/api/user'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'

interface Props {
  email: string
  onResend?: () => void
  pop?: (...args: any[]) => any
}

const InviteLink = ({ email, onResend, pop }: Props) => {
  const { data, error } = useGetUserInviteURL(email)
  const { t } = useTranslation(['user-invite'])
  const theme = useTheme()

  return (
    <Box display="flex" flexDirection="column">
      {error ? (
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
          <Box
            width={44}
            height={44}
            borderRadius={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor={getThemeColor(theme, MUIThemeColors.secondaryLight) + '44'}
            mb={1}
          >
            <LinkOff sx={{ fontSize: 22, color: getThemeColor(theme, MUIThemeColors.secondaryMain) }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={700} color={getThemeColor(theme, MUIThemeColors.black)}>
            {t('no-invitation-found-title')}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={400}
            mt={0.5}
            mb={1.5}
            color={getThemeColor(theme, MUIThemeColors.secondaryMain)}
          >
            {t('user-invite-not-found')}
          </Typography>
          {onResend && (
            <GridActionButton
              width="180px"
              height="40px"
              shadowWeight={null}
              bgColor={getThemeColor(theme, MUIThemeColors.primaryDark)}
              textColor={getThemeColor(theme, MUIThemeColors.white)}
              buttonText={t('resend-invitation')}
              onClick={() => {
                pop?.()
                onResend()
              }}
            />
          )}
        </Box>
      ) : (
        data && (
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <Box
              width={44}
              height={44}
              borderRadius={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor={getThemeColor(theme, MUIThemeColors.infoLight) + '44'}
              mb={1}
            >
              <Link sx={{ fontSize: 22, color: getThemeColor(theme, MUIThemeColors.primaryDark) }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} color={getThemeColor(theme, MUIThemeColors.black)}>
              {t('invite-link-ready-title')}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={400}
              mt={0.5}
              mb={1.5}
              color={getThemeColor(theme, MUIThemeColors.secondaryMain)}
            >
              {t('invite-link-ready-description')}
            </Typography>
            <FilledInput
              fullWidth
              value={data}
              sx={{
                backgroundColor: getThemeColor(theme, MUIThemeColors.infoLight) + '88',
                ':hover': {
                  backgroundColor: getThemeColor(theme, MUIThemeColors.infoLight),
                },
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      navigator.clipboard
                        .writeText(data)
                        .then(() => {
                          invalidateSystemUserCache.getSystemUsers()
                          toast.success(t('success-get-invite'))
                        })
                        .catch(() => toast.error(t('failed-get-invite')))
                    }}
                  >
                    <CopyAll sx={{ color: getThemeColor(theme, MUIThemeColors.primaryDark) }} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
        )
      )}
    </Box>
  )
}

export default InviteLink
